import {
  GoogleAuthProvider,
  browserLocalPersistence,
  connectAuthEmulator,
  getAuth,
  onAuthStateChanged,
  setPersistence,
  signInWithPopup,
  signOut,
} from "firebase/auth"
import type { Auth, User } from "firebase/auth"
import { getFirebaseApp, isFirebaseEmulator } from "./config"

/*
 * Facilitator sign-in. The only place `firebase/auth` is imported.
 *
 * Guests do NOT sign in. They enter a name and get an id this browser generates
 * (see `utils/sessionIdentity`), because Firebase Auth has one current user per
 * browser profile: an anonymous guest sign-in in a second tab would replace the
 * facilitator's Google session in the first, and a facilitator running the room
 * with a guest window open on the same machine is the normal case, not an edge
 * one.
 */

/**
 * The domain a facilitator's Google account must be on.
 *
 * Enforced in three places, none of which is redundant: `hd` below asks Google
 * to only OFFER accounts on it, the check in `facilitatorFrom` refuses anything
 * else the client ends up with, and the Firestore rules refuse its writes. Only
 * the last is a security boundary — the first two exist so the failure is a
 * message rather than a silent permission denial three screens later.
 */
export const FACILITATOR_DOMAIN: string = import.meta.env.VITE_FACILITATOR_DOMAIN || "loka.com"

/** A signed-in facilitator, reduced to what the app stores and renders. */
export interface AuthUser {
  uid: string
  email: string
  displayName: string
  photoUrl: string
}

/**
 * Whether a facilitator has to really sign in.
 *
 * `VITE_FACILITATOR_AUTH_REQUIRED=false` replaces the Google flow with the
 * single local account below, already "signed in" before the first render. It
 * exists so the facilitator screens can be opened and reviewed without a popup
 * and without a Google account — a person on a plane, or an agent driving the
 * app in a headless browser.
 *
 * It is NOT a way in: `firestore.rules` still refuses every write and every
 * customer read from an unauthenticated client, so this mode reaches the screens
 * and whatever is publicly readable (a session by its link), nothing more. Only
 * the default — anything other than the exact string "false" — is a real
 * boundary. Opt in per checkout in `.env.local`; never in a deployed build.
 */
export const FACILITATOR_AUTH_REQUIRED: boolean =
  import.meta.env.VITE_FACILITATOR_AUTH_REQUIRED !== "false"

/**
 * The stand-in facilitator used when sign-in is not required.
 *
 * A fixed uid rather than a generated one so the same local account is seen on
 * every reload and in every browser: anything it manages to write stays
 * readable to the next run, and two people comparing notes are looking at the
 * same account. The address is on the allowed domain so `isAllowedEmail` and
 * every downstream check treat it like any other facilitator.
 */
export const LOCAL_FACILITATOR: AuthUser = {
  uid: "local-facilitator",
  email: `local@${FACILITATOR_DOMAIN}`,
  displayName: "Local facilitator",
  photoUrl: "",
}

/*
 * Local-mode state. The four exported functions below are the whole auth
 * surface, so branching inside them keeps every caller — `authSync`, the store,
 * the guard — unaware that there are two modes at all.
 *
 * Sign-out is honoured rather than ignored, and the listeners are notified, so
 * the sign-in screen and the account button still behave like themselves: this
 * is a mode for LOOKING at those screens, and one that could not render them
 * would defeat the point.
 */
let localUser: AuthUser | null = LOCAL_FACILITATOR
const localListeners = new Set<(user: AuthUser | null) => void>()

function emitLocalUser(): void {
  for (const listener of localListeners) listener(localUser)
}

/** Thrown when a Google account signs in from outside the allowed domain. */
export class DomainNotAllowedError extends Error {
  constructor(readonly email: string) {
    super(
      `${email || "That account"} is not a ${FACILITATOR_DOMAIN} account. ` +
        `Sign in with your ${FACILITATOR_DOMAIN} Google account.`
    )
    this.name = "DomainNotAllowedError"
  }
}

export function getFirebaseAuth(): Auth {
  const auth = getAuth(getFirebaseApp())
  if (isFirebaseEmulator()) connectAuthEmulatorOnce(auth)
  return auth
}

// Dev only. Guarded on globalThis so repeated calls and HMR reloads connect
// once — connectAuthEmulator is not idempotent. `disableWarnings` silences the
// loud banner the SDK prints for emulator auth.
const authEmulatorScope = globalThis as unknown as { __authEmulated?: boolean }
function connectAuthEmulatorOnce(auth: Auth): void {
  if (authEmulatorScope.__authEmulated) return
  authEmulatorScope.__authEmulated = true
  connectAuthEmulator(auth, "http://127.0.0.1:9099", { disableWarnings: true })
}

export function isAllowedEmail(email: string | null | undefined): boolean {
  return typeof email === "string" && email.toLowerCase().endsWith(`@${FACILITATOR_DOMAIN}`)
}

function facilitatorFrom(user: User): AuthUser {
  return {
    uid: user.uid,
    email: user.email ?? "",
    displayName: user.displayName ?? user.email ?? "",
    photoUrl: user.photoURL ?? "",
  }
}

/**
 * Opens the Google account chooser and returns the signed-in facilitator.
 *
 * `prompt: "select_account"` is deliberate: without it a browser already signed
 * into a personal Google account signs straight back in with it and the user
 * never sees why they were rejected.
 *
 * An account from the wrong domain is signed straight back OUT before throwing.
 * Left signed in, it would sit in persistence and be restored on every reload —
 * so the app would boot into a rejected state with no way to reach the button
 * that fixes it.
 */
export async function signInWithGoogle(): Promise<AuthUser> {
  if (!FACILITATOR_AUTH_REQUIRED) {
    localUser = LOCAL_FACILITATOR
    emitLocalUser()
    return localUser
  }

  const auth = getFirebaseAuth()
  // Survives a reload and a new tab, which is what a facilitator expects from a
  // workspace. Set before the popup so the first sign-in is persisted too.
  await setPersistence(auth, browserLocalPersistence)

  const provider = new GoogleAuthProvider()
  provider.setCustomParameters({ hd: FACILITATOR_DOMAIN, prompt: "select_account" })

  const credential = await signInWithPopup(auth, provider)
  if (!isAllowedEmail(credential.user.email)) {
    const email = credential.user.email ?? ""
    await signOut(auth)
    throw new DomainNotAllowedError(email)
  }
  return facilitatorFrom(credential.user)
}

export async function signOutFacilitator(): Promise<void> {
  if (!FACILITATOR_AUTH_REQUIRED) {
    localUser = null
    emitLocalUser()
    return
  }
  await signOut(getFirebaseAuth())
}

/**
 * Reports who is signed in, now and whenever it changes. Returns the
 * unsubscribe.
 *
 * The domain is re-checked on every callback, not only at sign-in: persistence
 * restores whatever was there last, including an account from before the domain
 * was configured, and a token can outlive the rule that let it in. A rejected
 * account is signed out here as well, so the app never holds one.
 */
export function observeAuth(
  onChange: (user: AuthUser | null) => void,
  onRejected?: (error: DomainNotAllowedError) => void
): () => void {
  if (!FACILITATOR_AUTH_REQUIRED) {
    localListeners.add(onChange)
    // Answered on a task rather than inline: the store starts at "checking" and
    // `startAuth` runs inside an effect, so a synchronous callback would resolve
    // the status during the same commit that mounted the observer.
    void Promise.resolve().then(() => {
      if (localListeners.has(onChange)) onChange(localUser)
    })
    return () => localListeners.delete(onChange)
  }

  return onAuthStateChanged(getFirebaseAuth(), (user) => {
    if (!user) {
      onChange(null)
      return
    }
    if (!isAllowedEmail(user.email)) {
      const rejection = new DomainNotAllowedError(user.email ?? "")
      void signOut(getFirebaseAuth())
      onChange(null)
      onRejected?.(rejection)
      return
    }
    onChange(facilitatorFrom(user))
  })
}
